

var Model = require("../types/Model.js");
var par = require("par");
const pageNames = require("../../constants/PageNames.json");


/**
 * This is the main model for desktop applications. It holds all the state necessary for the app.
 * @type {Model}
 */
module.exports = Model({
	name: "atouchawaymobile",
	init: init,
	listeners: listeners,
	triggers: triggers,

	_has_associated: false,
	_show_urgency: null,

	set_page: set_page,
	make_sound_trigger: make_sound_trigger,
	stop_sounds: stop_sounds
});

function init() {
	/*
	 * This is the initial state. This is what things look like initially
	 */
	this.push({
		loading: false,
		messaging: {},
		banners: [],
		calling: {}, // The state of the calling model
		contacts: [], // The array of contacts from ContactList
		missed: {}, // Map of missed calls from specific users
		header: {}, // The header contents
		availability: {}, // The availability of all the people getting tracked
		page: "contacts", // What the main page is currently showing
		config: this.config, // The config passed in during creation
		localization: {}, // The current localization file
		footer: [], //array of footer content
		groupedmessages: [],
		personal: {}, // State of personal settings
		groups: {},
		latest: {}, // The state of LatestMessages. A map of UserID-Message.
		offline: false, // Whether the machine is offline
		conferenceinviter: {}, // The current state of the conference call inviter
		settings: {}, // The aTouchAway settings
		options: {}, // Options for the trunk
		hide: false, // Whether the screen should be hidden or not
		theme: "", // The state of the aTouchAway theme (red-white)
		bracelet: {}, // The state of the connected bracelet
		activity: {}, // the patients own activity
		bloodsugar: {}, //the patients blood sugar
		bloodpressure: {}, //the patients blood pressure
		bloodoxygen: {}, //the patients blood oxygen
		bodytemperature: {}, //the patients body temperature
		weight: {}, //the patients weight
		customindicator: [],
		customindicatorslist: [],
		urgency: {}, // The urgency call state
		reminders: null, // The state of the reminders
		trial: {},
		toast: null, // The state of the toast notification
		callinputswitcher: {},
		careplan: {},
		orientation: "",
		size: {},
		toosmall: false,
		screenlocker: false,
		keepawake: false,
		screensaver: false,
		aboutbox: {},
		newreading: {},
		myhealthdevice: false,
		comment: {},
		surveys: {},
		coreforms: {},
		workflowaction: false,
		feedbackmodal: false,
		messagingcontacts: {},
		keyboard: false,
		forms: [],
		formsubmissions: [],
		orggroup: null,
		activeformsubmission: null,
		pausescreen: false,
		fileviewer: {},
		pendingactions: [],
		callgroups: [],
		callgroupsmembers: {},
		callgroupscalling: {},
		status: null,
		workflowdefinition: [],
		resources: {},
		devices: [],
		metawearauto: false,
		devicefollowup: {},
		license: false,
		privacypolicy: false,
		workflowexternals: {},
		accountswitch: {},
		orderedforms: [],
		audiooutputselector: {},
		people: {},
		groupsettings: {},
		saving: false
	});

	this.emit("orientationlocker:trigger:lock", {
		orientation: "landscape"
	});

	this.emit("keepawake:trigger:lock");
	this.emit("screenlocker:trigger:lock");
	this.emit("coreforms:trigger:load");
}

/**
 * Sets the current page to be displayed
 * @param {String} page One of "calling", "contacts", "messaging"
 */
function set_page(page, options = {}) {
	const {dontClearMessaging = false} = options;
	var currentPage = this.data.page;
	if(currentPage === "messaging" && !dontClearMessaging){
		this.emit("atouchawaymessaging:trigger:view:cancel");}
	this.extend({
		page: page
	});
}

function listeners() {
	var sound = make_sound_trigger.bind(this);

	return {
		"banner:updated": makeExtender("banners"),
		"calling:updated": handle_calling_update,
		"buzz:state:receiving": sound("incoming", true),
		"buzz:state:calling": sound("outgoing", true),
		"buzz:state:busy": sound("busy"),
		"buzz:state:idle": stop_sounds,
		"buzz:state:connected": stop_sounds,
		"buzz:state:connecting": stop_sounds,
		"meetings:start": sound("incoming", true),
		"meetings:stop": stop_sounds,
		"online:updated": handle_online_update,
		"localization:updated": handle_localization_update,
		"atouchawaysettings:updated": makeExtender("settings"),
		"availability:updated": makeExtender("availability"),
		"activeownformsubmission:updated": makeExtender("activeformsubmission"),
		"contactspage:updated": makeExtender("contacts"),
		"groups:updated": makeExtender("groups"),
		"missed:updated": makeExtender("missed"),
		"footer:updated": makeExtender("footer"),
		"fileviewer:updated": makeExtender("fileviewer"),
		"latestmessages:updated": makeExtender("latest"),
		"conferenceinviter:updated": makeExtender("conferenceinviter"),
		"atouchawaymessaging:updated": makeExtender("messaging"),
		"atouchawaymessaging:opened": par(set_page, "messaging", { dontClearMessaging: true }),
		"atouchawaygroupedmessages:updated": makeExtender("groupedmessages"),
		"personal:updated": makeExtender("personal"),
		"theme:updated": makeExtender("theme"),
		"screenhider:updated": makeExtender("hide"),
		"screensaver:updated": makeExtender("screensaver"),
		"options:updated": makeExtender("options"),
		"bracelet:updated": makeExtender("bracelet"),
		"bloodoxygen:updated": makeExtender("bloodoxygen"),
		"bloodoxygen:viewing": par(set_page, "bloodoxygen"),
		"bodytemperature:updated": makeExtender("bodytemperature"),
		"bodytemperature:viewing": par(set_page, "bodytemperature"),
		"weight:updated": makeExtender("weight"),
		"weight:viewing": par(set_page, "weight"),
		"customindicator:updated": makeExtender("customindicator"),
		"customindicator:viewing": par(set_page, "customindicator"),
		"customindicatorslist:updated": makeExtender("customindicatorslist"),
		"bloodpressure:updated": makeExtender("bloodpressure"),
		"bloodpressure:viewing": par(set_page, "bloodpressure"),
		"bloodsugar:updated": makeExtender("bloodsugar"),
		"bloodsugar:viewing": par(set_page, "bloodsugar"),
		"activity:updated": makeExtender("activity"),
		"activity:viewing": par(set_page, "activity"),
		"overview:updated": makeExtender("overview"),
		"overview:viewing": par(set_page, "overview"),
		"newreading:updated": makeExtender("newreading"),
		"myhealthdevice:updated": makeExtender("myhealthdevice"),
		"comment:updated": makeExtender("comment"),
		"urgencycalls:updated": makeExtender("urgency"),
		"activereminder:updated": makeExtender("reminders"),
		"activereminder:active": sound("alert"),
		"trialdata:updated": makeExtender("trial"),
		"messagingcontacts:updated": makeExtender("messagingcontacts"),
		"keyboard:updated": makeExtender("keyboard"),
		"pausescreen:updated": makeExtender("pausescreen"),
		"machinetracker:updated": check_should_associate_machine,
		"callinputswitcher:updated": makeExtender("callinputswitcher"),
		"toast:updated": makeExtender("toast"),
		"screenorientation:updated": makeExtender("screenorientation"),
		"careplan:updated": makeExtender("careplan"),
		"careplan:viewing": handle_careplan_view,
		"orientation:updated": Model.makeExtender("orientation"),
		"size:updated": Model.makeExtender("size"),
		"toosmall:updated": Model.makeExtender("toosmall"),
		"about:updated": Model.makeExtender("aboutbox"),
		"about:unlocked": par(set_page, "setting"),
		"about:settings": par(set_page, "setting"),
		"about:exit": par(set_page, "contacts"),
		"atouchawaysettings:hidecareplan": handle_hide_careplan,
		"atouchawaysettings:hideforms": handle_hide_forms,
		"atouchawaysettings:hidemyhealth": handle_hide_myhealth,
		"atouchawaysettings:hideworkflow": handle_hide_workflow,
		"atouchawaysettings:hidestatus": handle_hide_status,
		"ownstatus:hidestatus": handle_hide_status,
		"atouchawaysettings:hideresources": handle_hide_resources,
		"surveyprocessing:updated": Model.makeExtender("surveys"),
		"coreforms:updated": Model.makeExtender("coreforms"),
		"usersmanagerlist:updated": Model.makeExtender("managerlist"),
		"screenlocker:updated": Model.makeExtender("screenlocker"),
		"keepawake:updated": Model.makeExtender("keepawake"),
		"feedbackmodal:updated": Model.makeExtender("feedbackmodal"),
		"activeworkflowaction:updated": Model.makeExtender("workflowaction"),
		"activeworkflowaction:viewing": sound("alert"),
		"ownformlist:updated": makeExtender("forms"),
		"ownformsubmissions:updated": makeExtender("formsubmissions"),
		"ownformsubmissions:viewing": view_forms,
		"orggroup:updated": makeExtender("orggroup"),
		"workflowactionpending:updated": makeExtender("pendingactions"),
		"callgroups:updated": makeExtender("callgroups"),
		"callgroupsmembers:updated": makeExtender("callgroupsmembers"),
		"callgroupscalling:updated": makeExtender("callgroupscalling"),
		"ownstatus:updated": makeExtender("status"),
		"workflowdefinitions:updated": makeExtender("workflowdefinitions"),
		"ownresources:updated": makeExtender("resources"),
		"ownresources:open": par(set_page, "resources"),
		"owndevices:updated": makeExtender("devices"),
		"metawearscheduler:updated": makeExtender("metawearauto"),
		"devicefollowup:updated": makeExtender("devicefollowup"),
		"license:updated": makeExtender("license"),
		"privacypolicy:updated": makeExtender("privacypolicy"),
		"workflowexternals:updated": makeExtender("workflowexternals"),
		"accountswitcher:updated": makeExtender("accountswitch"),
		"ownformorderedlist:updated": makeExtender("orderedforms"),
		"audiooutputselector:updated": makeExtender("audiooutputselector"),
		"people:updated": makeExtender("people"),
		"ownresources:resource:hidden": par(set_page, "contacts"),
		"ownformsubmissions:form:hidden": par(set_page, "contacts"),
		"groupsettings:updated": makeExtender("groupsettings"),
		"save:updated": makeExtender("saving"),
	};
}

function triggers() {
	return {
		"page": handle_trigger_page,
	};
}

function make_sound_trigger(sound, loop) {
	return function () {
		this.emit("sounds:trigger:play", {
			sound: sound,
			loop: loop
		});
	}.bind(this);
}

function stop_sounds() {
	this.emit("sounds:trigger:stop");
}

/**
 * Handles changes in calling. If calls are being made, switches page to calling.
 * When calls go idle, go back to contacts
 * @param  {Object} calling The state of calling
 */
function handle_calling_update(calling) {
	var extender = {
		calling: calling
	};
	var state = calling.state;

	if ((state === "calling") || (state === "receiving") || (state === "busy"))
		extender.page = "calling";
	else if (state === "idle" && this.data.page === "calling")
		extender.page = "contacts";

	extender.offline = (state === "offline");

	this.extend(extender);

	this.emit("machinetracker:trigger:state", calling);
}

/**
 * Handles trigger for changing the page
 * @param  {Object} data Data containing the `page` to switch to.
 */
function handle_trigger_page(data) {
	var page = data.page;
	const options = {};
	if (data.dontClearMessaging) {
		options.dontClearMessaging = data.dontClearMessaging;
	}
	delete data.dontClearMessaging;
	this.set_page(page, options);
	this.emit("usermetrics:trigger:track", pageNames?.atouchaway?.[page] || `navigate to atouchaway ${page} page (automatically generated)`);
}

function handle_online_update(data) {
	var status = !data; // offline status is opposite of online status.
	this.extend({
		offline: status
	});
}

function handle_localization_update(localization) {
	this.extend({
		localization: localization.localization
	});
}

function makeExtender(name) {
	return function (data) {
		var update = {};
		update[name] = data;
		this.extend(update);
	};
}

function handle_careplan_view(data) {
	var section = data.section;

	this.set_page("careplan_" + section);
}

function check_should_associate_machine() {
	if (this._has_associated) return;
	this.emit("machinetracker:trigger:associate");
	this._has_associated = true;
}

function handle_hide_careplan() {
	if(this.data.page.indexOf("careplan") === -1) return;
	this.set_page("contacts");
}

function handle_hide_forms(){
	if(this.data.page === "forms")
		this.set_page("contacts");
}

function view_forms() {
	if(this.data.page !== "forms")
		this.set_page("forms");
}

function handle_hide_workflow(){
	if(this.data.page === "workflow")
		this.set_page("contacts");
}

function handle_hide_status(){
	if(this.data.page === "status")
		this.set_page("contacts");
}

function handle_hide_resources(){
	if(this.data.page === "resources")
		this.set_page("contacts");
}

function handle_hide_myhealth(){
	var myhealthpages = [
		"overview",
		"activity",
		"bloodsugar",
		"weight",
		"bloodpressure",
		"bloodoxygen",
		"bodytemperature",
		"customindicator"
	];
	if(myhealthpages.indexOf(this.data.page) !== -1)
		this.set_page("contacts");
}
